import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, AutocompleteChangeReason, CircularProgress, TextField } from "@mui/material";

import { TravelExpenseSuggestedAuditCommentApiService } from "../../../../../../../../../../../services/travel-expense-suggested-audit-comment/travel-expense-suggested-audit-comment-api.service";
import { TravelExpenseSuggestedAuditCommentFilter } from "../../../../../../../../../../../models/travel-expense-suggested-audit-comment/travel-expense-suggested-audit-comment-filter";

const PAGE_SIZE = 5;

export enum CommentsTypes {
  isRelatedToCostIncurred = "isRelatedToCostIncurred",
  isRelatedToAmountExpenseTypeJustification = "isRelatedToAmountExpenseTypeJustification",
  isRelatedToReceiptCompliance = "isRelatedToReceiptCompliance",
}

interface Props {
  label: string;
  customComment: string;
  handleChangeField: (value: string) => void;
  commentFor: CommentsTypes;
  sxProps?: object;
  disabled?: boolean;
  travelExpenseTypeId?: string;
}

interface State {
  query: string;
  prevQuery: string;
  value: string;
  loading: boolean;
  error: string;
  open: boolean;
  items: string[];
  totalItems: number;
  pageNumber: number;
}

export const CommentField = ({
  label,
  handleChangeField,
  commentFor,
  customComment = "",
  disabled,
  sxProps,
  travelExpenseTypeId,
}: Props) => {
  const [state, setState] = useState<State>({
    query: "",
    prevQuery: "",
    value: customComment ?? "",
    loading: false,
    error: "",
    open: false,
    items: [],
    totalItems: 0,
    pageNumber: 0,
  });

  const customCommentFieldUpdated = useRef<boolean>(false);

  const handleChangePage = (pageNumber: number): void => setState((state) => ({ ...state, pageNumber }));

  const handleChangeQuery = (query: string): void =>
    setState((state) => ({
      ...state,
      pageNumber: 0,
      prevQuery: state.query,
      query,
    }));

  const loadData = useCallback(
    async (query: string = "", pageNumber: number = 0, signal: AbortSignal) => {
      const response = await new TravelExpenseSuggestedAuditCommentApiService().loadItems(
        {
          isRelatedToCostIncurred: commentFor === CommentsTypes.isRelatedToCostIncurred,
          isRelatedToAmountExpenseTypeJustification:
            commentFor === CommentsTypes.isRelatedToAmountExpenseTypeJustification,
          isRelatedToReceiptCompliance: commentFor === CommentsTypes.isRelatedToReceiptCompliance,
          travelExpenseTypeId: travelExpenseTypeId,
        } as TravelExpenseSuggestedAuditCommentFilter,
        query,
        [],
        {
          pageNumber,
          pageSize: PAGE_SIZE,
        },
        false,
        60,
        signal
      );

      return response;
    },
    [commentFor, travelExpenseTypeId]
  );

  // loadData ---- ON INIT
  const loadDataOnInit = useCallback(
    async (query: string, signal: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadData(query, 0, signal);

        if (!signal.aborted)
          setState((state) => ({
            ...state,
            loading: false,
            error,
            items: items?.map((item) => item.comment),
            totalItems,
          }));
      } catch (error: any) {
        if (!signal.aborted) setState((state) => ({ ...state, loading: false, error }));
      } finally {
        if (query !== state.value) {
          customCommentFieldUpdated.current = true;
          handleChangeField(query);
        }
      }
    },
    [loadData]
  );

  // loadData --- ON SCROLL
  const loadDataOnScroll = useCallback(
    async (pageNumber: number, signal: AbortSignal): Promise<void> => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadData(state.query, pageNumber, signal);

        if (!signal.aborted)
          setState((state) => ({
            ...state,
            loading: false,
            error,
            items: [...state.items, ...items?.map((item) => item.comment)],
            totalItems,
          }));
      } catch (error: any) {
        if (!signal.aborted) setState((state) => ({ ...state, loading: false, error }));
      }
    },
    [loadData, state.query]
  );

  const setOpen = (value: boolean): void => setState((state) => ({ ...state, open: value }));
  // reset state on close AutocompleteDropdown
  const closeAndReset = useCallback(() => {
    setState((state) => ({
      ...state,
      open: false,
      pageNumber: 0,
      items: [],
      totalItems: 0,
    }));

    state.query !== customComment && handleChangeField(state.query);
  }, [state.query, customComment]);

  useEffect(() => {
    if (customComment) {
      setState((state) => ({
        ...state,
        value: customComment,
        query: customComment,
        prevQuery: customComment,
      }));
    } else {
      setState((state) => ({
        ...state,
        value: "",
        query: "",
        prevQuery: "",
      }));
    }
  }, [customComment]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    if (state.open) {
      if (customCommentFieldUpdated.current) {
        customCommentFieldUpdated.current = false;

        return;
      }

      // onInit
      if (state.pageNumber === 0 && state.query === state.prevQuery) {
        loadDataOnInit(state.query, abortController.signal);
      }

      // onSearch
      if (state.pageNumber === 0 && state.query !== state.prevQuery) {
        timer = setTimeout(() => loadDataOnInit(state.query, abortController.signal), 500);
      }

      // onScroll
      if (state.pageNumber > 0) {
        loadDataOnScroll(state.pageNumber, abortController.signal);
      }
    }

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [
    state.open,
    state.query,
    state.prevQuery,
    state.value,
    state.pageNumber,
    loadDataOnInit,
    loadDataOnScroll,
    customCommentFieldUpdated,
  ]);

  const handleChangeValue = (value: string, reason: AutocompleteChangeReason) => {
    setState((state) => ({
      ...state,
      value,
      ...(reason === "clear" ? { query: "", prevQuery: "" } : {}),
    }));

    reason === "clear" && handleChangeField("");
  };

  const handleInputChange = (newValue: string) => {
    handleChangeField(newValue);
  };

  return (
    <Autocomplete
      id="customCommentSearchField"
      freeSolo
      open={state.open}
      disabled={disabled}
      onOpen={() => !disabled && setOpen(true)}
      loading={state.loading}
      onClose={closeAndReset}
      value={state.value}
      sx={{ ...sxProps }}
      filterOptions={(options) => options}
      onChange={(_, value, reason) => {
        handleChangeField(value ?? "");
        handleChangeValue(value ?? "", reason);
      }}
      onInputChange={(_, value) => {
        handleChangeQuery(value);
      }}
      ListboxProps={{
        onScroll: ({ currentTarget: { scrollTop, clientHeight, scrollHeight } }: React.SyntheticEvent) => {
          const { pageNumber, totalItems } = state;
          const maxPages = Math.ceil(totalItems / PAGE_SIZE);

          if (scrollHeight - Math.ceil(scrollTop) <= clientHeight && pageNumber < maxPages - 1 && !state.loading)
            handleChangePage(pageNumber + 1);
        },
        role: "list-box",
        style: { maxHeight: "150px", background: "white" },
      }}
      isOptionEqualToValue={(option, value) => option === value}
      getOptionLabel={(option) => {
        return option;
      }}
      options={!!state.items.length ? state.items : ["No comments found"]}
      getOptionDisabled={(option: string) => option === "No comments found"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            disabled: disabled,
            endAdornment: (
              <>
                {state.loading && <CircularProgress size={15} />}
                {!disabled && params.InputProps.endAdornment}
              </>
            ),
          }}
          error={!!state.error}
          helperText={state.error ? "Something went wrong loading suggested audit comments." : ""}
        />
      )}
    />
  );
};
